/**
 * Helpdesk — admin view of resident complaints.
 *
 * Admin can filter by status, click row to drill into a detail dialog,
 * and update status / priority / resolution notes inline. Realtime
 * `complaint:raised` events bump a live banner so the page feels alive.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  LifeBuoy,
  AlertCircle,
  CheckCircle2,
  Clock3,
  Sparkles,
  Wrench,
  Zap,
  Sparkle,
  ShieldAlert,
  Trees,
  ArrowUp,
  Car,
  Bug,
  Building,
  HelpCircle,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { get, patch, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AdvancedListToolbar,
  useUrlSearchState,
  type FilterChip
} from '@/components/ui/advanced-list-toolbar';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/format';
import { subscribe } from '@/lib/socket';

type Category =
  | 'plumbing'
  | 'electrical'
  | 'housekeeping'
  | 'security'
  | 'garden'
  | 'lift'
  | 'parking'
  | 'pest_control'
  | 'amenity'
  | 'other';
type Status = 'open' | 'assigned' | 'in_progress' | 'resolved' | 'closed' | 'rejected';
type Priority = 'low' | 'normal' | 'high' | 'urgent';

interface Complaint {
  id: string;
  society_id: string;
  resident_id: string;
  unit_id: string | null;
  category: Category;
  priority: Priority;
  title: string;
  body: string;
  photo_url: string | null;
  status: Status;
  assigned_admin_id: string | null;
  resolution_notes: string | null;
  resident_rating: number | null;
  sla_due_at: string | null;
  assigned_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ListResponse {
  items: Complaint[];
  next_cursor: string | null;
}

const CATEGORY_LABEL: Record<Category, string> = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  housekeeping: 'Housekeeping',
  security: 'Security',
  garden: 'Garden',
  lift: 'Lift',
  parking: 'Parking',
  pest_control: 'Pest control',
  amenity: 'Amenity',
  other: 'Other'
};

const CATEGORY_ICON: Record<Category, React.ComponentType<React.SVGProps<SVGSVGElement> & { strokeWidth?: number | string }>> = {
  plumbing: Wrench,
  electrical: Zap,
  housekeeping: Sparkle,
  security: ShieldAlert,
  garden: Trees,
  lift: ArrowUp,
  parking: Car,
  pest_control: Bug,
  amenity: Building,
  other: HelpCircle
};

const STATUS_LABEL: Record<Status, string> = {
  open: 'Open',
  assigned: 'Assigned',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
  rejected: 'Rejected'
};

const STATUS_VARIANT: Record<Status, 'info' | 'warn' | 'alert' | 'ok' | 'muted'> = {
  open: 'alert',
  assigned: 'info',
  in_progress: 'warn',
  resolved: 'ok',
  closed: 'muted',
  rejected: 'muted'
};

const PRIORITY_LABEL: Record<Priority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent'
};

const PRIORITY_VARIANT: Record<Priority, 'info' | 'warn' | 'alert' | 'ok' | 'muted'> = {
  low: 'muted',
  normal: 'info',
  high: 'warn',
  urgent: 'alert'
};

// URL-synced filter+sort+page state for the helpdesk list. Defaults dropped
// from the address bar so a fresh /helpdesk URL stays clean.
const COMPLAINT_FILTER_DEFAULTS = {
  search: '',
  status: '',
  category: '',
  priority: '',
  unit_id: '',
  sort: 'created_desc',
  page_size: 50
};

interface UnitLite {
  id: string;
  block_id: string | null;
  number: string;
}

interface BlockLite {
  id: string;
  name: string;
}

const UpdateSchema = z.object({
  status: z.enum(['open', 'assigned', 'in_progress', 'resolved', 'closed', 'rejected']).optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  resolution_notes: z.string().max(1000).optional()
});
type UpdateForm = z.infer<typeof UpdateSchema>;

// V0.3 — vendor assignment + resolve sub-schemas (live inline with the helpdesk
// detail drawer so admins can route complaints to a society-empanelled vendor).
type SlaPriority = 'low' | 'normal' | 'high' | 'urgent';
type VendorCategory =
  | 'plumbing' | 'electrical' | 'carpentry' | 'ac_repair' | 'painting'
  | 'pest_control' | 'housekeeping' | 'gardening' | 'lift_maintenance'
  | 'security_system' | 'other';

interface VendorLite {
  id: string;
  name: string;
  category: VendorCategory;
  status: 'active' | 'inactive' | 'blacklisted';
}

interface VendorAssignmentLite {
  id: string;
  vendor_id: string;
  complaint_id: string;
  sla_priority: SlaPriority;
  sla_due_at: string;
  assigned_at: string;
  resolved_at: string | null;
  vendor?: { id: string; name: string; category: VendorCategory; phone: string };
}

const SLA_LABEL: Record<SlaPriority, string> = {
  low: 'Low · 7d',
  normal: 'Normal · 48h',
  high: 'High · 24h',
  urgent: 'Urgent · 4h'
};
const SLA_VARIANT: Record<SlaPriority, 'muted' | 'info' | 'warn' | 'alert'> = {
  low: 'muted',
  normal: 'info',
  high: 'warn',
  urgent: 'alert'
};

const AssignVendorSchema = z.object({
  vendor_id: z.string().uuid('Select a vendor'),
  sla_priority: z.enum(['low', 'normal', 'high', 'urgent'])
});
type AssignVendorForm = z.infer<typeof AssignVendorSchema>;

const ResolveSchema = z.object({
  resolution_notes: z.string().trim().min(1, 'Notes required').max(500),
  resident_rating: z.coerce.number().int().min(1).max(5).optional()
});
type ResolveForm = z.infer<typeof ResolveSchema>;

export function HelpdeskPage(): JSX.Element {
  const [filterState, setFilterState] = useUrlSearchState(COMPLAINT_FILTER_DEFAULTS);
  const [searchInput, setSearchInput] = useState<string>(filterState.search);
  const [drillId, setDrillId] = useState<string | null>(null);
  const [liveCount, setLiveCount] = useState(0);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const flashIds = useRef<Set<string>>(new Set());

  // Debounce free-text search → URL state to avoid one refetch per keystroke.
  // Other filter changes (status / category / priority / unit / sort) refetch
  // immediately because each is one click.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput !== filterState.search) {
        setFilterState({ search: searchInput });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Stable hash for pagination reset on filter change.
  const filtersHash = useMemo(
    () =>
      JSON.stringify([
        filterState.search,
        filterState.status,
        filterState.category,
        filterState.priority,
        filterState.unit_id,
        filterState.sort,
        filterState.page_size
      ]),
    [filterState]
  );

  // Build the server query params from the URL state. Empty / default values
  // are stripped before the call so the request stays small.
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      limit: filterState.page_size,
      sort: filterState.sort
    };
    if (filterState.search) params.q = filterState.search;
    if (filterState.status) params.status = filterState.status;
    if (filterState.category) params.category = filterState.category;
    if (filterState.priority) params.priority = filterState.priority;
    if (filterState.unit_id) params.unit_id = filterState.unit_id;
    return params;
  }, [filterState]);

  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);
  const list = useQuery({
    queryKey: ['complaints', queryParams, cursor],
    queryFn: () =>
      get<ListResponse>('/complaints', {
        params: { ...queryParams, ...(cursor ? { cursor } : {}) }
      }),
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<Complaint>(list.data, cursor, filtersHash);

  // Units + blocks for the unit_id filter chip. Cheap calls, cached by react-query.
  const blocksQuery = useQuery({
    queryKey: ['society', 'blocks'],
    queryFn: () => get<{ items: BlockLite[] }>('/society/blocks')
  });
  const unitsQuery = useQuery({
    queryKey: ['society', 'units', 'all-helpdesk'],
    queryFn: () => get<{ items: UnitLite[] }>('/society/units', { params: { limit: 200 } })
  });

  const blockNameById = useMemo(() => {
    const map = new Map<string, string>();
    (blocksQuery.data?.items ?? []).forEach((b) => map.set(b.id, b.name));
    return map;
  }, [blocksQuery.data]);

  const unitOptions = useMemo(() => {
    const units = unitsQuery.data?.items ?? [];
    return [...units]
      .map((u) => ({
        id: u.id,
        label: `${u.block_id ? blockNameById.get(u.block_id) ?? '?' : '—'} / ${u.number}`,
        sortKey: `${u.block_id ? blockNameById.get(u.block_id) ?? 'zzz' : 'zzz'}-${u.number}`
      }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true }));
  }, [unitsQuery.data, blockNameById]);

  const items = paginator.items;
  const drillComplaint = useMemo(
    () => (drillId ? items.find((c) => c.id === drillId) ?? null : null),
    [drillId, items]
  );

  const form = useForm<UpdateForm>({
    resolver: zodResolver(UpdateSchema),
    defaultValues: { status: undefined, priority: undefined, resolution_notes: '' }
  });

  useEffect(() => {
    if (drillComplaint) {
      form.reset({
        status: drillComplaint.status,
        priority: drillComplaint.priority,
        resolution_notes: drillComplaint.resolution_notes ?? ''
      });
    }
  }, [drillComplaint, form]);

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: UpdateForm }) =>
      patch<Complaint, UpdateForm>(`/complaints/${id}`, {
        ...values,
        resolution_notes: values.resolution_notes?.trim() ? values.resolution_notes : undefined
      }),
    onSuccess: (c) => {
      toast.success(`Complaint ${STATUS_LABEL[c.status].toLowerCase()}`);
      void queryClient.invalidateQueries({ queryKey: ['complaints'] });
      setDrillId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── V0.3 vendor assignment integration ─────
  const vendorsForAssign = useQuery({
    queryKey: ['vendors', 'helpdesk-assign'],
    queryFn: () =>
      get<{ items: VendorLite[] }>('/vendors', { params: { status: 'active', limit: 100 } }),
    enabled: assignDialogOpen
  });

  const assignmentForCurrent = useQuery({
    queryKey: ['vendor-assignments', 'complaint', drillId],
    queryFn: () =>
      get<{ items: VendorAssignmentLite[] }>('/vendor-assignments', {
        params: drillId ? { complaint_id: drillId, limit: 5 } : { limit: 1 }
      }),
    enabled: Boolean(drillId)
  });
  const activeAssignment = useMemo<VendorAssignmentLite | null>(() => {
    const items = assignmentForCurrent.data?.items ?? [];
    return items.find((a) => a.resolved_at === null) ?? items[0] ?? null;
  }, [assignmentForCurrent.data]);

  const assignForm = useForm<AssignVendorForm>({
    resolver: zodResolver(AssignVendorSchema),
    defaultValues: { vendor_id: '', sla_priority: 'normal' }
  });

  const assignVendor = useMutation({
    mutationFn: (v: AssignVendorForm) => {
      if (!drillId) throw new Error('No complaint selected');
      return post(`/complaints/${drillId}/assign-vendor`, v);
    },
    onSuccess: () => {
      toast.success('Vendor assigned');
      setAssignDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['complaints'] });
      void queryClient.invalidateQueries({ queryKey: ['vendor-assignments'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const resolveForm = useForm<ResolveForm>({
    resolver: zodResolver(ResolveSchema),
    defaultValues: { resolution_notes: '', resident_rating: undefined }
  });

  const resolveAssignmentMutation = useMutation({
    mutationFn: (v: ResolveForm) => {
      if (!activeAssignment) throw new Error('No assignment');
      return post(`/vendor-assignments/${activeAssignment.id}/resolve`, {
        resolution_notes: v.resolution_notes,
        resident_rating: v.resident_rating
      });
    },
    onSuccess: () => {
      toast.success('Assignment resolved');
      setResolveDialogOpen(false);
      setDrillId(null);
      void queryClient.invalidateQueries({ queryKey: ['complaints'] });
      void queryClient.invalidateQueries({ queryKey: ['vendor-assignments'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const openAssignDialog = (): void => {
    assignForm.reset({ vendor_id: '', sla_priority: 'normal' });
    setAssignDialogOpen(true);
  };
  const openResolveDialog = (): void => {
    resolveForm.reset({ resolution_notes: '', resident_rating: undefined });
    setResolveDialogOpen(true);
  };

  // Realtime — bump banner + flash row on new complaint
  useEffect(() => {
    const offRaised = subscribe('complaint:raised', (e) => {
      if (e.type !== 'complaint:raised') return;
      flashIds.current.add(e.complaint_id);
      setLiveCount((c) => c + 1);
      void queryClient.invalidateQueries({ queryKey: ['complaints'] });
      toast.info('New complaint raised', { description: `Category: ${e.category}` });
      setTimeout(() => {
        flashIds.current.delete(e.complaint_id);
      }, 4000);
    });
    const offUpdated = subscribe('complaint:updated', () => {
      void queryClient.invalidateQueries({ queryKey: ['complaints'] });
    });
    return () => {
      offRaised();
      offUpdated();
    };
  }, [queryClient]);

  const onSubmit = (v: UpdateForm) => {
    if (!drillId) return;
    update.mutate({ id: drillId, values: v });
  };

  // KPIs
  const kpiOpen = items.filter((c) => c.status === 'open' || c.status === 'assigned' || c.status === 'in_progress').length;
  const kpiResolved = items.filter((c) => c.status === 'resolved' || c.status === 'closed').length;
  const kpiUrgent = items.filter((c) => c.priority === 'urgent').length;

  // Sort + filter chip wiring for the shared toolbar.
  const sortOptions = useMemo(
    () => [
      { value: 'created_desc', label: 'Created (newest)' },
      { value: 'created_asc', label: 'Created (oldest)' },
      { value: 'priority_desc', label: 'Priority (urgent first)' },
      { value: 'updated_desc', label: 'Updated (newest)' }
    ],
    []
  );

  const filterChips: FilterChip[] = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        type: 'select',
        value: filterState.status,
        options: [
          { value: '', label: 'All' },
          { value: 'open', label: 'Open' },
          { value: 'assigned', label: 'Assigned' },
          { value: 'in_progress', label: 'In progress' },
          { value: 'resolved', label: 'Resolved' },
          { value: 'closed', label: 'Closed' },
          { value: 'rejected', label: 'Rejected' }
        ],
        onChange: (v) => setFilterState({ status: v })
      },
      {
        id: 'category',
        label: 'Category',
        type: 'select',
        value: filterState.category,
        options: [
          { value: '', label: 'All' },
          { value: 'plumbing', label: 'Plumbing' },
          { value: 'electrical', label: 'Electrical' },
          { value: 'housekeeping', label: 'Housekeeping' },
          { value: 'security', label: 'Security' },
          { value: 'garden', label: 'Garden' },
          { value: 'lift', label: 'Lift' },
          { value: 'parking', label: 'Parking' },
          { value: 'pest_control', label: 'Pest control' },
          { value: 'amenity', label: 'Amenity' },
          { value: 'other', label: 'Other' }
        ],
        onChange: (v) => setFilterState({ category: v })
      },
      {
        id: 'priority',
        label: 'Priority',
        type: 'select',
        value: filterState.priority,
        options: [
          { value: '', label: 'All' },
          { value: 'low', label: 'Low' },
          { value: 'normal', label: 'Normal' },
          { value: 'high', label: 'High' },
          { value: 'urgent', label: 'Urgent' }
        ],
        onChange: (v) => setFilterState({ priority: v })
      },
      {
        id: 'unit_id',
        label: 'Unit',
        type: 'select',
        value: filterState.unit_id,
        options: [
          { value: '', label: 'All units' },
          ...unitOptions.map((u) => ({ value: u.id, label: u.label }))
        ],
        onChange: (v) => setFilterState({ unit_id: v })
      }
    ],
    [filterState, unitOptions, setFilterState]
  );

  const hasActiveFilters =
    filterState.search !== COMPLAINT_FILTER_DEFAULTS.search ||
    filterState.status !== COMPLAINT_FILTER_DEFAULTS.status ||
    filterState.category !== COMPLAINT_FILTER_DEFAULTS.category ||
    filterState.priority !== COMPLAINT_FILTER_DEFAULTS.priority ||
    filterState.unit_id !== COMPLAINT_FILTER_DEFAULTS.unit_id ||
    filterState.sort !== COMPLAINT_FILTER_DEFAULTS.sort ||
    filterState.page_size !== COMPLAINT_FILTER_DEFAULTS.page_size;

  const resetFilters = (): void => {
    setFilterState(COMPLAINT_FILTER_DEFAULTS);
    setSearchInput('');
  };

  return (
    <>
      <PageHeader
        title="Helpdesk"
        description="Resident complaints — assign, prioritise, and resolve."
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <LifeBuoy className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Open / in progress</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : kpiOpen}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <CheckCircle2 className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Resolved / closed</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : kpiResolved}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-alert-100)] text-vt-alert">
                <AlertCircle className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Urgent</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : kpiUrgent}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Live banner */}
        {liveCount > 0 ? (
          <div className="flex items-center justify-between rounded-vt-md border border-vt-primary-200 bg-vt-primary-100 px-vt-4 py-vt-2 text-vt-13 text-vt-primary">
            <span className="inline-flex items-center gap-vt-2">
              <Sparkles className="h-4 w-4" />
              {liveCount} new complaint{liveCount === 1 ? '' : 's'} since you opened this page
            </span>
            <Button variant="ghost" size="sm" onClick={() => setLiveCount(0)}>
              Dismiss
            </Button>
          </div>
        ) : null}

        {/* Advanced filter toolbar — search + filters + sort + page size */}
        <AdvancedListToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search by title or body…"
          filters={filterChips}
          sort={{
            value: filterState.sort,
            onChange: (v) => setFilterState({ sort: v }),
            options: sortOptions
          }}
          pageSize={filterState.page_size}
          onPageSizeChange={(n) => setFilterState({ page_size: n })}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
          resultLabel={
            list.data
              ? `${items.length} complaint${items.length === 1 ? '' : 's'}`
              : 'Loading…'
          }
        />

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {list.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <EmptyState
                icon={<LifeBuoy className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load complaints"
                description={getErrorMessage(list.error)}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : items.length === 0 ? (
              hasActiveFilters ? (
                <EmptyState
                  icon={<LifeBuoy className="h-6 w-6" strokeWidth={1.75} />}
                  title="No complaints match your filters"
                  description="Try widening the filters or clear them to see every ticket."
                  action={
                    <Button variant="secondary" onClick={resetFilters}>
                      Reset filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<LifeBuoy className="h-6 w-6" strokeWidth={1.75} />}
                  title="No complaints"
                  description="Residents have not raised any tickets yet."
                />
              )
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Raised</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((c) => {
                    const CatIcon = CATEGORY_ICON[c.category];
                    const flash = flashIds.current.has(c.id);
                    return (
                      <TableRow
                        key={c.id}
                        onClick={() => setDrillId(c.id)}
                        className={
                          'cursor-pointer transition-colors ' +
                          (flash ? 'bg-vt-primary-100/60 ' : '') +
                          'hover:bg-vt-surface-2'
                        }
                      >
                        <TableCell>
                          <div className="flex items-start gap-vt-3">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-surface-2 text-vt-text-muted">
                              <CatIcon className="h-4 w-4" strokeWidth={1.75} />
                            </span>
                            <div className="min-w-0">
                              <div className="truncate font-medium text-vt-text">{c.title}</div>
                              <div className="line-clamp-1 text-vt-12 text-vt-text-muted">{c.body}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-vt-text-muted">{CATEGORY_LABEL[c.category]}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={PRIORITY_VARIANT[c.priority]}>{PRIORITY_LABEL[c.priority]}</Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-vt-text-muted">
                          <Clock3 className="mr-1 inline h-3.5 w-3.5 -translate-y-0.5" />
                          {formatDateTime(c.created_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <CursorPaginator
                hasMore={Boolean(paginator.nextCursor)}
                loading={list.isFetching}
                itemsShown={items.length}
                pageSize={filterState.page_size}
                onLoadMore={() => {
                  if (paginator.nextCursor) setCursor(paginator.nextCursor);
                }}
              />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Drill-down dialog */}
      <Dialog open={Boolean(drillId)} onOpenChange={(v) => (v ? null : setDrillId(null))}>
        <DialogContent className="max-w-2xl">
          {drillComplaint ? (
            <>
              <DialogHeader>
                <DialogTitle>{drillComplaint.title}</DialogTitle>
                <DialogDescription>
                  Raised {formatDateTime(drillComplaint.created_at)} · Category:{' '}
                  {CATEGORY_LABEL[drillComplaint.category]}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-vt-4 py-vt-2">
                <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3 text-vt-13 text-vt-text">
                  {drillComplaint.body}
                </div>

                {drillComplaint.photo_url ? (
                  <a
                    href={drillComplaint.photo_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-vt-12 text-vt-primary hover:underline"
                  >
                    View attached photo →
                  </a>
                ) : null}

                <div className="grid grid-cols-2 gap-vt-3 text-vt-12">
                  <div>
                    <div className="text-vt-text-muted">Current status</div>
                    <Badge variant={STATUS_VARIANT[drillComplaint.status]}>
                      {STATUS_LABEL[drillComplaint.status]}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-vt-text-muted">Current priority</div>
                    <Badge variant={PRIORITY_VARIANT[drillComplaint.priority]}>
                      {PRIORITY_LABEL[drillComplaint.priority]}
                    </Badge>
                  </div>
                  {drillComplaint.resolved_at ? (
                    <div>
                      <div className="text-vt-text-muted">Resolved</div>
                      <div className="text-vt-text">{formatDateTime(drillComplaint.resolved_at)}</div>
                    </div>
                  ) : null}
                  {drillComplaint.resident_rating ? (
                    <div>
                      <div className="text-vt-text-muted">Resident rating</div>
                      <div className="text-vt-text">{drillComplaint.resident_rating} / 5</div>
                    </div>
                  ) : null}
                </div>

                {/* V0.3 — vendor assignment panel */}
                <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
                  <div className="flex items-start justify-between gap-vt-3">
                    <div className="min-w-0">
                      <div className="text-vt-12 text-vt-text-muted">Vendor assignment</div>
                      {activeAssignment ? (
                        <div className="mt-1 space-y-1">
                          <div className="flex flex-wrap items-center gap-vt-2 text-vt-13 text-vt-text">
                            <Wrench className="h-4 w-4 text-vt-primary" />
                            <span className="font-medium">{activeAssignment.vendor?.name ?? '—'}</span>
                            <Badge variant={SLA_VARIANT[activeAssignment.sla_priority]}>
                              {SLA_LABEL[activeAssignment.sla_priority]}
                            </Badge>
                          </div>
                          <div className="text-vt-12 text-vt-text-muted">
                            Due {formatDateTime(activeAssignment.sla_due_at)} · Assigned{' '}
                            {formatDateTime(activeAssignment.assigned_at)}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1 text-vt-12 text-vt-text-muted">
                          No vendor assigned yet.
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-vt-1">
                      {drillComplaint.status === 'open' && !activeAssignment ? (
                        <Button size="sm" variant="secondary" onClick={openAssignDialog}>
                          <UserPlus className="h-4 w-4" /> Assign vendor
                        </Button>
                      ) : null}
                      {activeAssignment && activeAssignment.resolved_at === null ? (
                        <Button size="sm" variant="secondary" onClick={openResolveDialog}>
                          <CheckCircle2 className="h-4 w-4" /> Resolve
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>

                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-vt-3 border-t border-vt-border pt-vt-3">
                  <div className="grid grid-cols-2 gap-vt-3">
                    <div className="flex flex-col gap-vt-1">
                      <Label htmlFor="status">Update status</Label>
                      <Select id="status" {...form.register('status')}>
                        <option value="open">Open</option>
                        <option value="assigned">Assigned</option>
                        <option value="in_progress">In progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                        <option value="rejected">Rejected</option>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-vt-1">
                      <Label htmlFor="priority">Priority</Label>
                      <Select id="priority" {...form.register('priority')}>
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </Select>
                    </div>
                  </div>
                  <div className="flex flex-col gap-vt-1">
                    <Label htmlFor="resolution_notes">Resolution notes</Label>
                    <Textarea
                      id="resolution_notes"
                      rows={3}
                      placeholder="Plumber dispatched, leak fixed at 4 PM"
                      {...form.register('resolution_notes')}
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setDrillId(null)}>
                      Close
                    </Button>
                    <Button type="submit" loading={update.isPending} loadingText="Saving…">
                      Save changes
                    </Button>
                  </DialogFooter>
                </form>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* V0.3 — assign vendor dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign vendor</DialogTitle>
            <DialogDescription>
              Route this complaint to a society-empanelled vendor and set an SLA.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={assignForm.handleSubmit((v) => assignVendor.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="av-vendor">Vendor</Label>
              <Select id="av-vendor" {...assignForm.register('vendor_id')}>
                <option value="">— Select vendor —</option>
                {(vendorsForAssign.data?.items ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} ({v.category.replace('_', ' ')})
                  </option>
                ))}
              </Select>
              {assignForm.formState.errors.vendor_id ? (
                <p className="text-vt-12 text-vt-alert">{assignForm.formState.errors.vendor_id.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label>SLA priority</Label>
              <div className="grid grid-cols-2 gap-vt-2 md:grid-cols-4">
                {(['low', 'normal', 'high', 'urgent'] as const).map((p) => (
                  <label
                    key={p}
                    className="flex cursor-pointer items-center gap-vt-2 rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13 hover:border-vt-border-strong"
                  >
                    <input
                      type="radio"
                      value={p}
                      {...assignForm.register('sla_priority')}
                      className="accent-vt-primary"
                    />
                    <span>{SLA_LABEL[p]}</span>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAssignDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={assignVendor.isPending}
                loadingText="Assigning…"
                disabled={assignVendor.isPending}
              >
                Assign
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* V0.3 — resolve assignment dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve assignment</DialogTitle>
            <DialogDescription>
              Record what was done and (optionally) the resident's satisfaction rating.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={resolveForm.handleSubmit((v) => resolveAssignmentMutation.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="rv-notes">Resolution notes</Label>
              <Textarea
                id="rv-notes"
                rows={3}
                placeholder="Plumber replaced sink valve; tested leak-free."
                {...resolveForm.register('resolution_notes')}
              />
              {resolveForm.formState.errors.resolution_notes ? (
                <p className="text-vt-12 text-vt-alert">
                  {resolveForm.formState.errors.resolution_notes.message}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="rv-rating">Resident rating (1-5, optional)</Label>
              <Select id="rv-rating" {...resolveForm.register('resident_rating')}>
                <option value="">—</option>
                <option value="1">1 - Poor</option>
                <option value="2">2 - Fair</option>
                <option value="3">3 - Average</option>
                <option value="4">4 - Good</option>
                <option value="5">5 - Excellent</option>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setResolveDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={resolveAssignmentMutation.isPending}
                loadingText="Saving…"
                disabled={resolveAssignmentMutation.isPending}
              >
                Mark resolved
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
